/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Audience Insights", description = "Audience Insights give marketers deeper understanding of the composition of their audiences.", lastUpdatedDate = new Date(1699228800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Audience Insights",
        description: "Audience Insights give marketers deeper understanding of the composition of their audiences.",
        lastUpdatedDate: "Mon Nov 06 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Customer Explorer",
          "depth": 1,
          "id": "customer-explorer"
        }, {
          "value": "Overlaps",
          "depth": 1,
          "id": "overlaps"
        }, {
          "value": "Breakdowns",
          "depth": 1,
          "id": "breakdowns"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }, {
          "value": "Can I use Audience Insights while creating an audience?",
          "depth": 1,
          "id": "can-i-use-audience-insights-while-creating-an-audience"
        }, {
          "value": "Is our insight data saved by Hightouch?",
          "depth": 1,
          "id": "is-our-insight-data-saved-by-hightouch"
        }, {
          "value": "Do Breakdowns and Overlaps reflect if an audience belongs to a priority list?",
          "depth": 1,
          "id": "do-breakdowns-and-overlaps-reflect-if-an-audience-belongs-to-a-priority-list"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      strong: "strong",
      h3: "h3",
      img: "img",
      h4: "h4",
      ol: "ol",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Customer Studio is available as an add-on on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier\nplans"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Modern marketing teams coordinate omnichannel efforts in Hightouch by strategically building customer segments and activating them from their data warehouse to various destinations like Facebook, Braze, Salesforce, Google, and more. When building large and complex audiences, it's often difficult to fully understand the users within the audiences."
      }), "\n", _jsxs(_components.p, {
        children: ["Customer Studio's ", _jsx(_components.strong, {
          children: "Audience Insights"
        }), " give marketing teams a clearer picture when creating or updating audiences.\nCustomer Studio also provides an ", _jsx(_components.a, {
          href: "/campaign-intelligence/performance",
          children: "analytics suite"
        }), " for more in-depth analysis."]
      }), "\n", _jsx(_components.h3, {
        id: "customer-explorer",
        children: "Customer Explorer"
      }), "\n", _jsxs(_components.p, {
        children: ["When building an audience, it's helpful to have concrete examples of what the audience members are like to ensure the audience you're building is the intended one. You can do this by selecting to ", _jsx(_components.strong, {
          children: "Preview results"
        }), " to the right of your conditions. The preview gives you an overview of the audience and the ability to more closely inspect members."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/usage/preview-audience.png",
          alt: "Previewing an audience in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The overview shows your audience size and a list of its members. If you've chosen to ", _jsx(_components.a, {
          href: "/customer-studio/schema#column-configuration",
          children: "redact any columns"
        }), " in your model configuration, they are redacted here."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/audience-preview.png",
          alt: "Previewing an audience in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Clicking on a particular member gives you audience member details."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/models/redacted-user.png",
          alt: "Previewing an audience member in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "overlaps",
        children: "Overlaps"
      }), "\n", _jsx(_components.p, {
        children: "With many audiences syncing simultaneously, it can be difficult to ensure that end users aren't being targeted by multiple campaigns."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Overlaps"
        }), " helps marketers validate the composition of their audiences by providing them with feedback on an audience's composition as they define their desired segmentation in Hightouch."]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information, check out the ", _jsxs(_components.a, {
          href: "https://hightouch.com/blog/feature-release-audience-overlaps",
          children: [_jsx(_components.strong, {
            children: "Audience Overlaps"
          }), " blog post"]
        }), "."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Audience Overlaps can only be set up between audiences that use the same\n", _jsx(_components.a, {
            href: "/customer-studio/schema#parent-models",
            children: "parent model"
          }), "."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Before you begin creating audiences, make sure you've set up ", _jsx(_components.a, {
            href: "/customer-studio/schema",
            children: "your data\nmodels"
          }), "."]
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Navigate to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/audiences",
            children: [_jsx(_components.strong, {
              children: "Audiences"
            }), " page"]
          }), ". Select ", _jsx(_components.strong, {
            children: "Add audience"
          }), " in the top right corner or click an existing audience."]
        }), "\n", _jsxs(_components.li, {
          children: ["Use the ", _jsx(_components.a, {
            href: "/customer-studio/usage#filter-conditions",
            children: "filters"
          }), " to build the audience."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on the ", _jsx(_components.strong, {
            children: "Overlap"
          }), " tab to the right of the filters.\n", _jsx(_components.img, {
            src: "hightouch-audiences/overlap-tab.png",
            alt: "Overlap tab"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a previously created audience from the dropdown and click the ", _jsx(_components.strong, {
            children: "Continue"
          }), " button to compare the audiences. You can compare several audiences at the same time.\n", _jsx(_components.img, {
            src: "hightouch-audiences/compared-audience.png",
            alt: "Compared audience"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "breakdowns",
        children: "Breakdowns"
      }), "\n", _jsx(_components.p, {
        children: "By enabling users to better understand the distribution of specific characteristics within their audiences, marketers can quickly QA segmentation and ensure that their audience needs are met each time an audience is created."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Breakdowns"
        }), " allows users to visually display the distribution of specific characteristics of a given audience as they define their desired segmentation in Hightouch."]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information, check out the ", _jsxs(_components.a, {
          href: "https://hightouch.com/blog/audience-breakdowns",
          children: [_jsx(_components.strong, {
            children: "Audience Breakdowns"
          }), " blog post"]
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "setup-1",
        children: "Setup"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Before you begin creating audiences, make sure you've set up ", _jsx(_components.a, {
            href: "/customer-studio/schema",
            children: "your data\nschema"
          }), "."]
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Navigate to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/audiences",
            children: [_jsx(_components.strong, {
              children: "Audiences"
            }), " page"]
          }), ". Select ", _jsx(_components.strong, {
            children: "Add audience"
          }), " in the top right corner or click an existing audience."]
        }), "\n", _jsxs(_components.li, {
          children: ["Use the ", _jsx(_components.a, {
            href: "/customer-studio/usage#filter-conditions",
            children: "filters"
          }), " to build the audience."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on the ", _jsx(_components.strong, {
            children: "Breakdown"
          }), " tab to the right of the filters.\n", _jsx(_components.img, {
            src: "hightouch-audiences/breakdown-tab.png",
            alt: "Breadown tab"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a column from the dropdown and click the ", _jsx(_components.strong, {
            children: "Continue"
          }), " button to visualize the value distribution. Several columns may be broken down at the same time.\n", _jsx(_components.img, {
            src: "hightouch-audiences/breakdowns.png",
            alt: "Breakdown visualizations"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "can-i-use-audience-insights-while-creating-an-audience",
        children: "Can I use Audience Insights while creating an audience?"
      }), "\n", _jsx(_components.p, {
        children: "Yes, you can use Insights when creating, cloning, and updating audiences."
      }), "\n", _jsx(_components.h3, {
        id: "is-our-insight-data-saved-by-hightouch",
        children: "Is our insight data saved by Hightouch?"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "No"
        }), ", Hightouch never saves your data."]
      }), "\n", _jsx(_components.h3, {
        id: "do-breakdowns-and-overlaps-reflect-if-an-audience-belongs-to-a-priority-list",
        children: "Do Breakdowns and Overlaps reflect if an audience belongs to a priority list?"
      }), "\n", _jsxs(_components.p, {
        children: ["Yes-the insight tools take ", _jsx(_components.a, {
          href: "/customer-studio/priority-lists",
          children: "audience priority"
        }), " into account.\nTo learn more, read ", _jsx(_components.a, {
          href: "/customer-studio/priority-lists",
          children: "the priority lists docs"
        }), " or navigate to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/priority-lists",
          children: [_jsx(_components.strong, {
            children: "Priority Lists"
          }), " page"]
        }), " page and build your first priority list."]
      })]
    });
  }
}
export default MDXContent;
